import React, { useState, useRef, useEffect } from 'react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, set, push } from 'firebase/database';
import { PhotoModel } from '../types/protocols';
import { formatDateForStorage } from '../Helpers/DateUtils';

// Helper function to get image dimensions
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            reject(new Error('Failed to load image'));
        };
        img.src = URL.createObjectURL(file);
    });
};

interface BlueprintUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (newBlueprintKey: string) => void;
    buildingKey: string;
    levelKey: string;
    authorUid: string;
}

const BlueprintUploadModal: React.FC<BlueprintUploadModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    buildingKey,
    levelKey,
    authorUid
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pdfPages, setPdfPages] = useState<string[]>([]);
    const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);
    const [isProcessingPdf, setIsProcessingPdf] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // PDF processing function using PDF.js from CDN
    const processPdfToImages = async (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    
                    // Load PDF.js from CDN
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
                    script.onload = async () => {
                        try {
                            // Set worker source
                            (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
                            
                            const pdfDoc = await (window as any).pdfjsLib.getDocument(arrayBuffer).promise;
                            const pageImages: string[] = [];
                            
                            for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
                                const page = await pdfDoc.getPage(pageNum);
                                const viewport = page.getViewport({ scale: 2.0 });
                                
                                const canvas = document.createElement('canvas');
                                const context = canvas.getContext('2d');
                                if (!context) {
                                    reject(new Error('Could not get canvas context'));
                                    return;
                                }
                                
                                canvas.height = viewport.height;
                                canvas.width = viewport.width;
                                
                                await page.render({
                                    canvasContext: context,
                                    viewport: viewport
                                }).promise;
                                
                                pageImages.push(canvas.toDataURL('image/png'));
                            }
                            
                            resolve(pageImages);
                        } catch (error) {
                            reject(error);
                        }
                    };
                    script.onerror = () => reject(new Error('Failed to load PDF.js library'));
                    document.head.appendChild(script);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read PDF file'));
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setError(null);
            setPdfPages([]);
            setSelectedPageIndex(0);
            
            // Check file type
            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            
            if (!isImage && !isPDF) {
                setError('Endast bilder (PNG, JPG, JPEG) och PDF-filer är tillåtna');
                return;
            }
            
            if (isPDF) {
                setIsProcessingPdf(true);
                try {
                    const pages = await processPdfToImages(file);
                    setPdfPages(pages);
                    setSelectedFile(file);
                } catch (error) {
                    setError('Fel vid bearbetning av PDF-fil: ' + (error instanceof Error ? error.message : 'Okänt fel'));
                    setIsProcessingPdf(false);
                    return;
                }
                setIsProcessingPdf(false);
            } else {
                setSelectedFile(file);
            }
        }
    };

    // Convert selected PDF page to File object
    const convertSelectedPdfPageToFile = async (): Promise<File> => {
        if (pdfPages.length === 0 || selectedPageIndex >= pdfPages.length) {
            throw new Error('No PDF page selected');
        }
        
        const dataUrl = pdfPages[selectedPageIndex];
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        
        return new File([blob], `blueprint_page_${selectedPageIndex + 1}.png`, {
            type: 'image/png',
            lastModified: Date.now()
        });
    };

    const uploadBlueprint = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setError(null);

        try {
            let fileToUpload = selectedFile;
            
            // If it's a PDF, convert the selected page to an image
            if (selectedFile.type === 'application/pdf') {
                fileToUpload = await convertSelectedPdfPageToFile();
            }

            // Generate new blueprint key
            const database = getDatabase();
            const blueprintsRef = dbRef(database, `buildings/${buildingKey}/structure/levels/${levelKey}/blueprints`);
            const newBlueprintRef = push(blueprintsRef);
            const newBlueprintKey = newBlueprintRef.key!;

            // Create storage path
            const storagePath = `buildings/${buildingKey}/structure/levels/${levelKey}/blueprints/${newBlueprintKey}`;

            // Upload to storage
            const storage = getStorage();
            const fileRef = storageRef(storage, storagePath);
            await uploadBytes(fileRef, fileToUpload);
            const downloadURL = await getDownloadURL(fileRef);

            // Get image dimensions
            const dimensions = await getImageDimensions(fileToUpload);

            // Create PhotoModel
            const photoModel: PhotoModel = {
                key: newBlueprintKey,
                author: authorUid,
                date: formatDateForStorage(new Date()),
                width: dimensions.width,
                height: dimensions.height,
                storagePath: storagePath,
                fileSize: fileToUpload.size
            };

            // Save to database
            await set(newBlueprintRef, photoModel);

            // Update currentBlueprintKey
            const levelRef = dbRef(database, `buildings/${buildingKey}/structure/levels/${levelKey}/currentBlueprintKey`);
            await set(levelRef, newBlueprintKey);

            onSuccess(newBlueprintKey);
            onClose();
        } catch (err) {
            console.error('Error uploading blueprint:', err);
            setError(err instanceof Error ? err.message : 'Ett fel uppstod vid uppladdning av ritning');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setError(null);
        setPdfPages([]);
        setSelectedPageIndex(0);
        setIsProcessingPdf(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '10px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Ladda upp ny ritning</h3>
                
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Välj ritning (PNG, JPG, JPEG eller PDF):
                    </label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileSelect}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '5px'
                        }}
                    />
                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        PDF-filer konverteras automatiskt till bilder. Välj vilken sida som ska användas som ritning.
                    </small>
                </div>

                {isProcessingPdf && (
                    <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ 
                                width: '20px', 
                                height: '20px', 
                                border: '2px solid #2196f3', 
                                borderTop: '2px solid transparent', 
                                borderRadius: '50%', 
                                animation: 'spin 1s linear infinite' 
                            }}></div>
                            Bearbetar PDF-fil...
                        </div>
                    </div>
                )}

                {selectedFile && (
                    <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <strong>Vald fil:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                )}

                {pdfPages.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                            Välj sida att använda som ritning:
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            {pdfPages.map((page, index) => (
                                <div
                                    key={index}
                                    onClick={() => setSelectedPageIndex(index)}
                                    style={{
                                        border: selectedPageIndex === index ? '3px solid #007bff' : '2px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '10px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedPageIndex === index ? '#e3f2fd' : 'white',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <img
                                        src={page}
                                        alt={`Sida ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '150px',
                                            objectFit: 'contain',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <div style={{ textAlign: 'center', marginTop: '8px', fontWeight: '500' }}>
                                        Sida {index + 1}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && (
                    <div style={{ 
                        marginBottom: '20px', 
                        padding: '10px', 
                        backgroundColor: '#f8d7da', 
                        color: '#721c24', 
                        borderRadius: '5px',
                        border: '1px solid #f5c6cb'
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleClose}
                        disabled={isUploading}
                        style={{
                            padding: '10px 20px',
                            border: '1px solid #ddd',
                            backgroundColor: 'white',
                            borderRadius: '5px',
                            cursor: isUploading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Avbryt
                    </button>
                    <button
                        onClick={uploadBlueprint}
                        disabled={!selectedFile || isUploading || isProcessingPdf || (selectedFile?.type === 'application/pdf' && pdfPages.length === 0)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            backgroundColor: (isUploading || isProcessingPdf || !selectedFile || (selectedFile?.type === 'application/pdf' && pdfPages.length === 0)) ? '#6c757d' : '#007bff',
                            color: 'white',
                            borderRadius: '5px',
                            cursor: (isUploading || isProcessingPdf || !selectedFile || (selectedFile?.type === 'application/pdf' && pdfPages.length === 0)) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isProcessingPdf ? 'Bearbetar PDF...' : isUploading ? 'Laddar upp...' : 'Ladda upp'}
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default BlueprintUploadModal;
